import { clearingApi } from "../../config/apiConfig";

export const getAllCustomers = async () => {
  try {
    const response = await clearingApi.get("/customer/all");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch customers", error);
    throw error;
  }
};

export const addCustomer = async (customerData) => {
  try {
    const response = await clearingApi.post("/customer", customerData);
    return response.data;
  } catch (error) {
    console.error("Error adding customer:", error);
    throw error;
  }
};
export const getCustomerById = async (id) => {
  try {
    const response = await clearingApi.get("/customer", {
      params: { id },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch customer by ID", error);
    throw error;
  }
};
export const updateCustomer = async (customerData) => {
  try {
    const response = await clearingApi.put("/customer", customerData, {
      params: { id: customerData.id },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating customer", error);
    throw error;
  }
};

export const deleteCustomerById = async (id) => {
  if (!id || id === "") {
    console.error(
      "Failed to delete customer because no customer id was provided to deleteCustomerById; \n id:",
      id
    );

    return false;
  }

  try {
    const response = await clearingApi.delete("/customer", {
      params: { id },
    });

    if (response?.status > 300) {
      console.error("Delete customer response not ok:", response);
      return { success: false, message: response?.data || "Failed to delete customer" };
    }

    return { success: true };
  } catch (error) {
    console.error("Failed to delete customer by ID", error);
    const errorMessage =
      typeof error.response?.data === "string"
        ? error.response.data
        : error.response?.data?.message || "Failed to delete customer";
    return { success: false, message: errorMessage };
  }
};

export const addStationWithCustomerId = async (formData) => {
  if (!formData?.stationId || formData?.stationId === "") {
    console.error("StationId is required for creating station with customer.");
    return false;
  }
  try {
    const response = await clearingApi.post("/station", formData);

    if (response?.status >= 300) {
      console.error(
        "Adding station (POST /station) response not OK:",
        response,
        "\n\n Adding station (POST /station) formData was:",
        formData
      );
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error adding station (POST /station):", error);
    return false;
  }
};

export const getStationsByCustomerId = async ({ customerId }) => {
  if (!customerId) {
    console.warn("Customer id not defined");
    return;
  }

  try {
    const response = await clearingApi.get(
      `station/customer?customer_id=${customerId}`
    );

    if (response?.status >= 300) {
      console.log("customer owned stations: ", response);
    }

    return response?.data?.map((st) => st?.stationId) || [];
  } catch (e) {
    console.error("Failed to get owned stations for user.", customerId, e);
  }
};

export const getCustomerByStationId = async (stationId) => {
  if (!stationId) {
    console.warn("Station id not defined");
    return;
  }

  try {
    const response = await clearingApi.get(
      `station?station_id=${stationId}`
    );

    return response?.data?.customerId;
  } catch (e) {
    console.error("Failed to get customer by station id.", stationId, e);
  }
};

export const assignStationToCustomer = async ({ customerId, stationId }) => {
  if (!customerId || !stationId) {
    console.warn(
      `[assignStationToCustomer] MISSING INFO: \n CustomerId: ${customerId} \n StationId: ${stationId}`
    );
    return;
  }

  try {
    const response = await clearingApi.post(`station`, {
      stationId,
      customerId,
    });

    if (response?.status >= 300) {
      console.log("add station to customer", response);
      return false;
    }

    return true;
  } catch (e) {
    console.error("Failed to assign station to customer.", customerId, e);
    return false;
  }
};

export const removeStationFromCustomer = async ({ stationId }) => {
  if (!stationId) {
    console.warn(
      `[removeStationFromCustomer] MISSING INFO: \n StationId: ${stationId}`
    );
    return false;
  }

  try {
    const response = await clearingApi.delete(
      `station?station_id=${stationId}`
    );

    if (response?.status >= 300) {
      console.log("rm station from customer", response);
      return false;
    }

    return true;
  } catch (e) {
    console.error("Failed to remove station from customer.", stationId, e);
    return false;
  }
};

export const getCustomerNames = async () => {
  const endpoint = "/customer/names";

  try {
    const response = await clearingApi.get(endpoint);

    if (response?.status >= 300) {
      console.log("getCustomerNames response", response);
      return false;
    }

    return response?.data || [];
  } catch (e) {
    console.error("Failed to getCustomerNames.", e);
    return [];
  }
};

export const getCustomerChangeHistory = async ({ customerId }) => {
  const endpoint = `audit/customers/${customerId}`;

  try {
    const response = await clearingApi.get(endpoint);

    if (response?.status >= 300) {
      console.log("get customer change history response", response);
      return null;
    }

    return response?.data || [];
  } catch (e) {
    console.error("Failed to get customer change history.", e);
    return [];
  }
};

// The backend answers a 400 with the reason as a plain string, so surface that
// rather than a generic message — "already has a VAT ID" is worth reading.
const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  return fallback;
};

/**
 * The missing-VAT-ID worklist. Customers taken off the list with a recorded
 * reason (CLR-276) are excluded unless `includeExcluded` is set, in which case
 * those rows carry a `vat_id_exclusion` object.
 */
export const getCustomersMissingVatId = async ({ includeExcluded = false } = {}) => {
  try {
    const response = await clearingApi.get("/customer/missing-vat-id", {
      params: includeExcluded ? { include_excluded: true } : undefined,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch customers missing VAT ID", error);
    throw error;
  }
};

/**
 * Takes a customer off the missing-VAT-ID worklist with a recorded reason.
 * Re-posting overwrites an existing exclusion, so a wrong reason can be
 * corrected without undoing first. Resolves to the updated customer.
 */
export const excludeCustomerFromMissingVatId = async (id, { reason, note }) => {
  try {
    const trimmedNote = typeof note === "string" ? note.trim() : "";
    const response = await clearingApi.post(`/customer/${id}/vat-id-exclusion`, {
      reason,
      note: trimmedNote || null,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to exclude customer from the missing VAT ID list", error);
    throw new Error(
      apiErrorMessage(error, "Failed to remove the customer from the list.")
    );
  }
};

/** Undoes an exclusion, putting the customer back on the worklist. */
export const removeCustomerVatIdExclusion = async (id) => {
  try {
    await clearingApi.delete(`/customer/${id}/vat-id-exclusion`);
  } catch (error) {
    console.error("Failed to undo the missing VAT ID exclusion", error);
    throw new Error(apiErrorMessage(error, "Failed to put the customer back on the list."));
  }
};

export const patchCustomerVatId = async (id, vatId) => {
  try {
    const response = await clearingApi.patch(`/customer/${id}/vat-id`, {
      vat_id: vatId,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to patch customer VAT ID", error);
    throw error;
  }
};
